// src/services/pcrService.ts
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp,
  DocumentReference,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { PCRForm } from '../types/formTypes';

// Create a new PCR form
export const createPCRForm = async (pcrData: Omit<PCRForm, 'timestamp' | 'lastSaved'>) => {
  try {
    const formData = {
      ...pcrData,
      timestamp: serverTimestamp(),
      lastSaved: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'pcrForms'), formData);
    return { id: docRef.id, success: true };
  } catch (error) {
    console.error("Error creating PCR form: ", error);
    return { error, success: false };
  }
};

// Update an existing PCR form
export const updatePCRForm = async (formId: string, pcrData: Partial<PCRForm>) => {
  try {
    const formRef = doc(db, 'pcrForms', formId);
    const updateData = {
      ...pcrData,
      lastSaved: serverTimestamp()
    };
    
    await updateDoc(formRef, updateData);
    return { success: true };
  } catch (error) {
    console.error("Error updating PCR form: ", error);
    return { error, success: false };
  }
};

// Get PCR form by ID
export const getPCRForm = async (formId: string) => {
  try {
    const formRef = doc(db, 'pcrForms', formId);
    const formSnap = await getDoc(formRef);
    
    if (formSnap.exists()) {
      const formData = formSnap.data() as PCRForm;
      
      // Make sure studentInfo fields have defaults if missing
      const formWithDefaults = {
        ...formData,
        studentName: formData.studentName || '',
        studentEmail: formData.studentEmail || '',
        studentId: formData.studentId || ''
      };
      
      return { 
        ...formWithDefaults,
        id: formSnap.id, 
        success: true 
      };
    } else {
      return { error: 'Form not found', success: false };
    }
  } catch (error) {
    console.error("Error getting PCR form: ", error);
    return { error, success: false };
  }
};

// Get all PCR forms for a user
export const getUserPCRForms = async (userId: string) => {
  try {
    const q = query(collection(db, 'pcrForms'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const forms = querySnapshot.docs.map(doc => {
      const data = doc.data() as PCRForm;
      
      // Use default empty strings for student info if not present
      return {
        ...data,
        id: doc.id,
        // Ensure these fields exist even if they're not in the data
        studentName: data.studentName || '',
        studentEmail: data.studentEmail || '',
        studentId: data.studentId || ''
      };
    });
    
    return { forms, success: true };
  } catch (error) {
    console.error("Error getting user PCR forms: ", error);
    return { error, success: false };
  }
};